import { EditorContent, useEditor } from '@tiptap/react'
import React, { useCallback, useEffect } from 'react'
import { AlignType, PaddingType } from 'common/types/styleTypes'
import { Toolbar } from './components/toolbar'
import { EditorExtensionEnum } from './enums/editor-extensions-enum'
import { TiptapEditorUi } from './ui/tiptap-editor-ui'
import { convertDraftToTiptap } from './utils/convert-draft-to-tiptap'
import { getExtensions } from './utils/get-extensions'
import { toggleParentDraggableAttribute } from './utils/toogle-parent-draggable-attribute'

export interface TextEditorUpdatePayload {
  content?: string
  fontSize?: number
  mobileFontSize?: number
  lineHeight?: number
  mobileLineHeight?: number
}

export interface TiptapEditorProps {
  extensions?: EditorExtensionEnum[]
  update: (payload: Partial<TextEditorUpdatePayload>) => void
  isEditing: boolean
  content: string
  linkColor?: string
  textColor?: string
  padding?: PaddingType
  mobilePadding?: PaddingType
  editorTextAlign?: AlignType
  textAlign: AlignType
  fontSize?: number
  mobileFontSize?: number
  fontWeight?: string
  mobileFontWeight?: string
  enableHeadings?: boolean
  attrId: string
  backgroundColor?: string
  mobileBackgroundColor?: string
  letterSpacing?: number
  mobileLetterSpacing?: number
  fontStyle?: string
  mobileFontStyle?: string
  lineHeight?: number
  mobileLineHeight?: number
  fontFamily?: string
  mobileFontFamily?: string
  isBlogPage?: boolean
}

export const TiptapEditor = ({
  update,
  isEditing,
  content = '',
  attrId,
  linkColor,
  textColor,
  fontSize,
  mobileFontSize,
  fontWeight,
  mobileFontWeight,
  padding,
  mobilePadding,
  textAlign,
  editorTextAlign,
  enableHeadings = false,
  backgroundColor,
  mobileBackgroundColor,
  letterSpacing,
  mobileLetterSpacing,
  lineHeight,
  mobileLineHeight,
  fontFamily,
  mobileFontFamily,
  fontStyle,
  mobileFontStyle,
  isBlogPage = false,
}: TiptapEditorProps) => {
  const editor = useEditor({
    extensions: getExtensions(enableHeadings),
    content: isDraftContent(content),
    editorProps: {
      attributes: {
        style: `text-align: ${editorTextAlign || 'left'};`,
      },
    },
  })
  const focusEditor = useCallback(() => {
    editor?.commands.focus()
    toggleParentDraggableAttribute(false)
  }, [editor])

  useEffect(() => {
    if (!isEditing) {
      toggleParentDraggableAttribute(true)
    }
  }, [isEditing])

  useEffect(() => {
    if (editor) {
      editor.commands.setTextAlign(editorTextAlign as string)
    }
  }, [editorTextAlign, editor])

  const onSave = (value: string) => {
    update({ content: value })
  }

  const handleBlur = () => {
    if (!editor) return
    onSave(editor.getHTML())
  }

  if (!editor) return null

  return (
    <>
      {isEditing && (
        <Toolbar
          update={update}
          enableHeadings={enableHeadings}
          editor={editor}
          isBlogPage={isBlogPage}
        />
      )}
      <TiptapEditorUi
        id={attrId}
        onClick={focusEditor}
        onBlur={handleBlur}
        isEditing={isEditing}
        textAlign={textAlign}
        padding={padding}
        mobilePadding={mobilePadding}
        lineHeight={lineHeight}
        mobileLineHeight={mobileLineHeight}
        fontFamily={fontFamily}
        mobileFontFamily={mobileFontFamily}
        fontSize={fontSize}
        mobileFontSize={mobileFontSize}
        fontStyle={fontStyle}
        mobileFontStyle={mobileFontStyle}
        fontWeight={fontWeight}
        mobileFontWeight={mobileFontWeight}
        linkColor={linkColor}
        color={textColor}
        backgroundColor={backgroundColor}
        mobileBackgroundColor={mobileBackgroundColor}
        letterSpacing={letterSpacing}
        mobileLetterSpacing={mobileLetterSpacing}
        enableHeadings={enableHeadings}
      >
        <EditorContent editor={editor} />
      </TiptapEditorUi>
    </>
  )
}

const isDraftContent = (content: string) =>
  typeof content === 'string' && content.startsWith('{')
    ? convertDraftToTiptap(content)
    : content
